import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  FaPlus, 
  FaTrash, 
  FaLink, 
  FaShieldAlt, 
  FaSync,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSearch,
  FaSort,
  FaCopy
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import FirewallCreateModal from './FirewallCreateModal';
import FirewallDetailsModal from './FirewallDetailsModal';
import FirewallRuleModal from './FirewallRuleModal';
import { useAuth } from '../../components/auth/AuthContext';

import FirewallAttachModal from './FirewallAttachModal';
import { api } from '../../services/api';
import Sidebar from '../../components/layout/Sidebar';
import { useResponsive } from '../../components/vm/useResponsive';
import * as Sentry from "@sentry/react";
import { useNotification } from '../../Notifications';

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'firewall-page' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'firewall-page' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'firewall-page',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'User profile not found. Please refresh and try again',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'INVALID_USER_PROFILE': 'Invalid user profile format. Please contact support',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again later',
  'DB_ERROR': 'Service temporarily unavailable. Please try again later',
  'API_ERROR': 'Service temporarily unavailable. Please try again later',
  'INVALID_API_RESPONSE': 'Service error. Please try again later',
  'API_CONNECTION_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'NETWORK_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'INVALID_ID_FORMAT': 'Invalid request format. Please try again',
  'INVALID_DATA': 'Invalid input data. Please check your entries and try again',
  'INVALID_REQUEST': 'Invalid request format. Please try again',
  'MISSING_FIELDS': 'Please fill in all required fields',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'INVALID_RULE_PARAMETERS': 'Invalid firewall rule parameters. Please check your input',
  'INVALID_JSON': 'Invalid request format. Please check your input',
  'UNEXPECTED_PAYLOAD': 'Unexpected request data. Please try again',
  'UNEXPECTED_BODY': 'Invalid request format. Please try again',
  'INVALID_RULE_ID_FORMAT': 'Invalid rule ID format. Please try again',
  'FIREWALL_LIST_FAILED': 'Unable to load firewalls. Please refresh the page',
  'FIREWALL_DETAILS_FAILED': 'Unable to load firewall details. Please try again',
  'FIREWALL_CREATE_FAILED': 'Unable to create firewall. Please try again',
  'FIREWALL_DELETE_FAILED': 'Unable to delete firewall. Please try again',
  'FIREWALL_NOT_FOUND': 'Firewall not found. It may have been deleted',
  'FIREWALL_LIMIT_EXCEEDED': 'You have reached the maximum limit of 50 firewalls. Delete unused firewalls to create new ones',
  'FIREWALL_HAS_ATTACHMENTS': 'Firewall is attached to VMs. Please detach before deleting',
  'FIREWALL_IN_USE': 'Firewall is currently in use. Please detach all instances before deleting',
  'RESOURCE_ALREADY_EXISTS': 'A firewall with this name already exists. Please choose a different name',
  'FIREWALL_RULE_ADD_FAILED': 'Unable to add firewall rule. Please try again',
  'FIREWALL_RULE_DELETE_FAILED': 'Unable to remove firewall rule. Please try again',
  'FIREWALL_RULE_NOT_FOUND': 'Firewall rule not found. It may have been removed',
  'SECURITY_RULE_ADD_FAILED': 'Unable to add security rule. Please try again',
  'SECURITY_RULE_DELETE_FAILED': 'Unable to remove security rule. Please try again',
  'SECURITY_RULE_ALREADY_EXISTS': 'This security rule already exists for this instance',
  'RULE_NOT_FOUND': 'Security rule not found. It may have been removed',
  'VM_LIST_FAILED': 'Unable to load virtual machines. Please refresh the page',
  'VM_NOT_FOUND': 'Virtual machine not found. It may have been deleted',
  'VM_NOT_ACTIVE': 'Virtual machine must be active to perform this action',
  'VM_FIREWALLS_FAILED': 'Unable to load VM firewall information. Please try again',
  'VM_EVENTS_FAILED': 'Unable to load VM events. Please try again',
  'FIREWALL_ATTACH_FAILED': 'Unable to attach firewall to VMs. Please try again',
  'NO_VMS_SELECTED': 'Please select at least one virtual machine',
  'INVALID_VM_IDS': 'Some selected VMs are invalid or not found',
  'VM_NOT_COMPATIBLE': 'Selected VMs are not compatible with this firewall (different regions)',
  'INSTANCE_NOT_FOUND': 'Instance not found. It may have been deleted',
  'INSTANCE_NOT_AVAILABLE': 'Instance is not available for this operation',
  'INSTANCE_NOT_COMPATIBLE': 'Selected instances are not compatible with this firewall',
  'INSTANCES_NOT_ACTIVE': 'Some instances are not active. Only active instances can be modified',
  'INSTANCE_NOT_ACTIVE': 'Instance must be active to perform this action',
  'INVALID_INSTANCE_ID_FORMAT': 'Invalid instance ID format. Expected format: BRK-XXXXXXD',
  'TIER_NOT_SUPPORTED': 'This operation is only supported for legacy tier instances',
  'REGION_NOT_AVAILABLE': 'Selected region is not available. Please choose a different region',
  'INVALID_REGION_FORMAT': 'Invalid region format. Please try again',
  'UNSUPPORTED_REGION': 'This region is not supported for firewalls',
  'PROTOCOLS_FAILED': 'Unable to load supported protocols. Please refresh the page',
  'REGIONS_FAILED': 'Unable to load available regions. Please refresh the page',
  // Network Errors (NEW)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Unable to reach server. Please try later.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Safe error message function
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return 'Something went wrong. Please try again.';
};

// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};
// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Items per page
const ITEMS_PER_PAGE = 15;

const FirewallPage = () => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();

  // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Performance: Granular loading states
  const [loadingStates, setLoadingStates] = useState({
    firewalls: false,
    environments: false,
    creating: false,
    deleting: new Set(),
    addingRule: new Set(),
    attaching: new Set()
  });
  
  const [firewalls, setFirewalls] = useState([]);
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [environments, setEnvironments] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Search and Sort
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  
  // Expanded row state
  const [expandedFirewallId, setExpandedFirewallId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  // Region state management
  const [regions] = useState(['CANADA-1']);
  const [selectedRegion, setSelectedRegion] = useState('CANADA-1');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef(null);
  
  // Delete confirmation modal states
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [firewallToDelete, setFirewallToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Performance: Request cancellation
  const abortControllerRef = useRef(null);
  
  // Operation refs for duplicate prevention
  const operationRefs = useRef({
    deleting: new Set()
  });
  
  const navigate = useNavigate();
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [selectedFirewall, setSelectedFirewall] = useState(null);

  // Sort options
  const sortOptions = [
    { id: 'name', label: 'Name' },
    { id: 'rules', label: 'Rules' },
    { id: 'created', label: 'Date' },
  ];

  // Filtered and sorted firewalls
  const filteredFirewalls = useMemo(() => {
    let result = firewalls.filter(fw => {
      return fw.environment?.region === selectedRegion || fw.region === selectedRegion;
    });
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(fw => 
        fw.name?.toLowerCase().includes(query) ||
        fw.id?.toString().toLowerCase().includes(query)
      );
    }
    
    result = [...result].sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'rules') {
        return (b.rules?.length || 0) - (a.rules?.length || 0);
      }
      if (sortBy === 'created') {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      }
      return 0;
    });
    
    return result;
  }, [firewalls, selectedRegion, searchQuery, sortBy]);

  // Pagination calculations
  const totalPages = useMemo(() => {
    return Math.ceil(filteredFirewalls.length / ITEMS_PER_PAGE);
  }, [filteredFirewalls.length]);

  const paginatedFirewalls = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredFirewalls.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredFirewalls, currentPage]);

  // Firewall limit check
  const isAtLimit = useMemo(() => {
    return filteredFirewalls.length >= 50;
  }, [filteredFirewalls.length]);

  // Reset page when search/sort/region changes
  useEffect(() => {
    setCurrentPage(1);
    setExpandedFirewallId(null);
  }, [searchQuery, sortBy, selectedRegion]);

  // Set user context for Sentry
  useEffect(() => {
    Sentry.setContext("firewall-page", {
      currentPage,
      firewallCount: firewalls.length,
      selectedRegion,
      isMobile,
      isTablet,
      isAtLimit
    });
  }, [currentPage, firewalls.length, selectedRegion, isMobile, isTablet, isAtLimit]);

  // Error notification useEffect
  useEffect(() => {
    if (error && error !== lastShownError) {
      notification.error(error);
      setLastShownError(error);
      setError(null);
    }
  }, [error, notification, lastShownError]);

  // Handle click outside for region dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Performance: Request cancellation cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending firewall requests');
      }
    };
  }, []);

  // Copy to clipboard
  const copyToClipboard = useCallback((id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    logUserAction('copy_firewall_id', { firewallId: id });
  }, []);

  // Toggle expanded row
  const handleToggleExpand = useCallback((fwId) => {
    setExpandedFirewallId(prev => prev === fwId ? null : fwId);
    logUserAction('toggle_firewall_expand', { firewallId: fwId });
  }, []);

  const debouncedFetchFirewalls = useMemo(
  () => debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating firewall fetch request', {
      currentPage,
      selectedRegion
    });
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    setLoadingStates(prev => ({ ...prev, firewalls: true }));
    setError(null);
    
    try {
      const response = await api.getFirewalls();
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (abortControllerRef.current?.signal.aborted) {
        logInfo('Firewall fetch request was aborted', { duration });
        return;
      }

      if (response.success) {
        const firewallData = response.firewalls || [];
        setFirewalls(Array.isArray(firewallData) ? firewallData : []);
        
        const environmentsMap = {};
        firewallData.forEach(firewall => {
          if (firewall.environment && firewall.environment.id) {
            environmentsMap[firewall.environment.id] = {
              id: firewall.environment.id,
              name: firewall.environment.name,
              region: firewall.environment.region
            };
          }
        });
        
        setEnvironments(Object.values(environmentsMap));
        
        if (!initialDataLoaded) setInitialDataLoaded(true);
        
        logPerformance('fetch_firewalls', duration, {
          firewallCount: firewallData.length,
          environmentCount: Object.keys(environmentsMap).length,
          selectedRegion,
          success: true
        });
        
        logInfo('Firewalls loaded successfully', {
          count: firewallData.length,
          environments: Object.keys(environmentsMap).length,
          duration: `${duration.toFixed(2)}ms`,
          selectedRegion
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        setFirewalls([]);
        
        logError('Failed to fetch firewalls', {
          response: response || 'No response',
          code: response?.code,
          duration: `${duration.toFixed(2)}ms`,
          selectedRegion
        });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        logInfo('Firewall fetch request aborted by user');
        return;
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'AUTH_ERROR') {
        logError('Authentication error during firewall fetch', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        });
        handleAuthError();
        return;
      }
      
      // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        setFirewalls([]);
        logError('Firewall fetch error', {
          code: err.code,
          status: err.status,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 3: Use interceptor's user-friendly message directly
      if (err.error) {
        setError(err.error);
        setFirewalls([]);
        logError('Firewall fetch error', {
          code: err.code,
          status: err.status,
          message: err.error,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 4: Fallback for unexpected error format
      setError('Something went wrong. Please try again.');
      setFirewalls([]);
      logError('Firewall fetch error (unexpected format)', {
        errorType: typeof err,
        duration: `${duration.toFixed(2)}ms`,
        currentPage,
        selectedRegion
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, firewalls: false }));
    }
  }, 300),
  [currentPage, initialDataLoaded, selectedRegion, handleAuthError]
);
  // Fetch firewalls on mount
  useEffect(() => {
    debouncedFetchFirewalls();
  }, [debouncedFetchFirewalls]);

  // Region change handlers
  const handleRegionChange = useCallback((region) => {
    logUserAction('region_changed', {
      fromRegion: selectedRegion,
      toRegion: region
    });
    
    setSelectedRegion(region);
    setIsRegionDropdownOpen(false);
    setCurrentPage(1);
  }, [selectedRegion]);
  
  const toggleRegionDropdown = useCallback((e) => {
    e.stopPropagation();
    setIsRegionDropdownOpen(prev => !prev);
  }, []);
const handleDeleteFirewall = useCallback(async () => {
  if (!firewallToDelete) return;
  
  const firewallId = firewallToDelete.id;
  const firewallName = firewallToDelete.name;
  
  if (operationRefs.current.deleting.has(firewallId)) {
    return;
  }
  
  const startTime = performance.now();
  
  operationRefs.current.deleting.add(firewallId);
  
  logUserAction('delete_firewall_confirmed', { firewallId, firewallName });
  logInfo('Starting firewall deletion', { firewallId, firewallName });
  
  setIsDeleting(true);
  setError(null);
  
  try {
    const response = await api.deleteFirewall(firewallId);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      setFirewalls(prevFirewalls => prevFirewalls.filter(fw => fw.id !== firewallId));
      setConfirmDeleteOpen(false);
      setFirewallToDelete(null);
      notification.success('Firewall deleted successfully');
      
      logPerformance('delete_firewall', duration, {
        firewallId,
        firewallName,
        success: true
      });
      
      logInfo('Firewall deleted successfully', {
        firewallId,
        firewallName,
        duration: `${duration.toFixed(2)}ms`
      });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      setError(safeMessage);
      
      logError('Failed to delete firewall', {
        firewallId,
        firewallName,
        code: response?.code,
        duration: `${duration.toFixed(2)}ms`
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      logError('Authentication error during firewall deletion', {
        firewallId,
        firewallName,
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      handleAuthError();
      return;
    }
    
    // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Delete firewall error', {
        firewallId,
        firewallName,
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Use interceptor's user-friendly message directly
    if (err.error) {
      setError(err.error);
      logError('Delete firewall error', {
        firewallId,
        firewallName,
        code: err.code,
        status: err.status,
        message: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Fallback for unexpected error format
    setError('Something went wrong. Please try again.');
    logError('Delete firewall error (unexpected format)', {
      firewallId,
      firewallName,
      errorType: typeof err,
      duration: `${duration.toFixed(2)}ms`
    }, err);
  } finally {
    operationRefs.current.deleting.delete(firewallId);
    setIsDeleting(false);
  }
}, [firewallToDelete, handleAuthError, notification]);
  // Handle opening delete confirmation modal
  const handleOpenDeleteModal = useCallback((firewall) => {
    logUserAction('delete_firewall_modal_opened', { 
      firewallId: firewall.id,
      firewallName: firewall.name
    });
    
    setFirewallToDelete(firewall);
    setConfirmDeleteOpen(true);
  }, []);

  // Handle create firewall modal
  const handleOpenCreateModal = useCallback(() => {
    if (isAtLimit) {
      logUserAction('create_firewall_blocked_by_limit', { 
        currentCount: filteredFirewalls.length,
        limit: 25,
        selectedRegion
      });
      return;
    }
    
    logUserAction('open_create_modal', { selectedRegion });
    setCreateModalOpen(true);
  }, [isAtLimit, filteredFirewalls.length, selectedRegion]);

  // Handle view details modal
  const handleViewDetails = useCallback((firewall) => {
    logUserAction('view_firewall_details', { 
      firewallId: firewall.id,
      firewallName: firewall.name
    });
    setSelectedFirewall(firewall);
    setDetailsModalOpen(true);
  }, []);

  // Handle add rule modal
  const handleAddRule = useCallback((firewall) => {
    logUserAction('add_firewall_rule_initiated', { 
      firewallId: firewall.id,
      firewallName: firewall.name
    });
    setSelectedFirewall(firewall);
    setRuleModalOpen(true);
  }, []);

  // Handle attach to VM modal
  const handleAttachToVM = useCallback((firewall) => {
    logUserAction('attach_firewall_initiated', { 
      firewallId: firewall.id,
      firewallName: firewall.name
    });
    setSelectedFirewall(firewall);
    setAttachModalOpen(true);
  }, []);

  // Handle firewall creation success
  const handleFirewallCreated = useCallback((newFirewall) => {
    logInfo('New firewall created successfully', {
      firewallId: newFirewall.id || 'pending',
      firewallName: newFirewall.name
    });
    setFirewalls(prevFirewalls => [...prevFirewalls, newFirewall]);
    setCreateModalOpen(false);
    notification.success('Firewall created successfully');
  }, [notification]);

  // Performance: Controlled refresh
  const maxRetries = 3;
  const retryAttempts = useRef(0);

  const handleRefresh = useCallback(async () => {
    if (retryAttempts.current >= maxRetries) {
      const errorMsg = 'Maximum refresh attempts reached. Please wait before trying again.';
      setError(errorMsg);
      logError('Maximum refresh attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries,
        selectedRegion
      });
      return;
    }
    
    retryAttempts.current++;
    
    logUserAction('refresh_firewalls', { 
      attempt: retryAttempts.current,
      maxRetries,
      selectedRegion
    });
    
    try {
      await debouncedFetchFirewalls();
      retryAttempts.current = 0;
    } catch (err) {
      if (retryAttempts.current >= maxRetries) {
        const safeMessage = getSafeErrorMessage(err);
        setError(safeMessage);
      }
    }
  }, [debouncedFetchFirewalls, selectedRegion]);

  // Shield Icon Component
  const ShieldIcon = ({ size = 20 }) => (
    <div style={{ width: size, height: size * 1.16, position: 'relative', flexShrink: 0 }}>
      <svg viewBox="0 0 24 28" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <path
          d="M12 0L0 4v10c0 6 5 12 12 14 7-2 12-8 12-14V4L12 0z"
          fill="#e0eacc"
        />
      </svg>
    </div>
  );

  // Render firewall row
  const renderFirewallRow = useCallback((firewall) => {
    const ruleCount = firewall.rules?.length || 0;
    const attachmentCount = firewall.attachments?.length || 0;
    const isExpanded = expandedFirewallId === firewall.id;

    return (
      <div key={firewall.id}>
        {/* Collapsed Row */}
        <div 
          className="flex items-center px-4 py-2.5 border-b border-[#222] hover:bg-[#1a1a1a] transition-colors cursor-pointer"
          style={{ borderLeft: '3px solid #6b7280' }}
        >
          <div 
            onClick={() => handleToggleExpand(firewall.id)}
            className="flex items-center gap-4 flex-1 min-w-0"
          >
            <ShieldIcon size={16} />
            <span className="text-white text-sm font-medium min-w-[160px] truncate">
              {sanitizeText(firewall.name)}
            </span>
            <span className="text-[#9ca3af] text-xs">
              {ruleCount} rule{ruleCount !== 1 ? 's' : ''}
            </span>
            <span className="text-[#6b7280] text-xs">
              {attachmentCount} VM{attachmentCount !== 1 ? 's' : ''}
            </span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 items-center">
            <button
              onClick={(e) => { e.stopPropagation(); handleAddRule(firewall); }}
              disabled={loadingStates.addingRule.has(firewall.id)}
              className="px-3 py-1.5 rounded text-xs flex items-center gap-1.5 bg-[#cad9e3] text-black hover:bg-[#b8c7d1] transition-colors"
            >
              {loadingStates.addingRule.has(firewall.id) ? (
                <>
                  <div className="animate-spin h-3 w-3 border-2 border-t-black border-black border-opacity-20 rounded-full"></div>
                  <span>Adding...</span>
                </>
              ) : (
                <span>Add Rule</span>
              )}
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); handleAttachToVM(firewall); }}
              disabled={loadingStates.attaching.has(firewall.id)}
              className="px-3 py-1.5 rounded text-xs flex items-center gap-1.5 bg-[#e0eacc] text-black hover:bg-[#c8d9a9] transition-colors"
            >
              {loadingStates.attaching.has(firewall.id) ? (
                <>
                  <div className="animate-spin h-3 w-3 border-2 border-t-black border-black border-opacity-20 rounded-full"></div>
                  <span>Attaching...</span>
                </>
              ) : (
                <>
                  <FaLink size={10} />
                  <span>Attach</span>
                </>
              )}
            </button>
          </div>
          
          {/* Expand chevron */}
          <div 
            onClick={() => handleToggleExpand(firewall.id)}
            className="ml-3 p-1 cursor-pointer"
          >
            <FaChevronRight 
              size={10} 
              className={`text-[#6b7280] transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            />
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-4 py-4 bg-[#0d0d0d] border-b border-[#222]">
            <div className="grid grid-cols-[1fr_auto] gap-4">
              {/* Details Grid - 3 columns */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <span className="text-[10px] text-[#6b7280] uppercase block mb-1">Firewall ID</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-white">{sanitizeText(firewall.id?.toString())}</span>
                    <button 
                      onClick={() => copyToClipboard(firewall.id?.toString())}
                      className="p-0.5 bg-transparent border-none cursor-pointer"
                    >
                      <FaCopy size={10} className={copiedId === firewall.id?.toString() ? 'text-[#a5c77f]' : 'text-[#6b7280]'} />
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-[10px] text-[#6b7280] uppercase block mb-1">Region</span>
                  <span className="text-xs text-white">{firewall.environment?.region || firewall.region || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-[10px] text-[#6b7280] uppercase block mb-1">Created</span>
                  <span className="text-xs text-white">
                    {firewall.created_at ? new Date(firewall.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={() => handleViewDetails(firewall)}
                  className="px-3.5 py-2 rounded-md text-xs flex items-center gap-2 justify-center min-w-[90px] bg-[#333] text-white hover:bg-[#444] transition-colors cursor-pointer border-none"
                >
                  <FaShieldAlt size={10} />
                  <span>Details</span>
                </button>
                <button
                  onClick={() => handleOpenDeleteModal(firewall)}
                  disabled={loadingStates.deleting.has(firewall.id)}
                  className="px-3.5 py-2 rounded-md text-xs flex items-center gap-2 justify-center min-w-[90px] bg-[rgba(238,85,85,0.1)] text-[#ff7777] border border-[#ff7777] hover:bg-[rgba(238,85,85,0.2)] transition-colors cursor-pointer"
                >
                  {loadingStates.deleting.has(firewall.id) ? (
                    <>
                      <div className="animate-spin h-3 w-3 border-2 border-t-[#ff7777] border-[#ff7777] border-opacity-20 rounded-full"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <FaTrash size={10} />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [expandedFirewallId, loadingStates, copiedId, handleToggleExpand, handleAddRule, handleAttachToVM, handleViewDetails, handleOpenDeleteModal, copyToClipboard]);

  // Delete Confirmation Modal
// Delete Confirmation Modal - Updated to match standard pattern
const ConfirmDeleteDialog = () => {
  if (!confirmDeleteOpen || !firewallToDelete) return null;
  
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Delete Firewall
            </h3>
          </div>
          <button
            onClick={() => {
              setConfirmDeleteOpen(false);
              setFirewallToDelete(null);
              logUserAction('delete_firewall_cancelled', {
                firewallId: firewallToDelete?.id
              });
            }}
            disabled={isDeleting}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to delete this firewall?
          </p>
          
          {/* Firewall Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Firewall Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(firewallToDelete.name)}
            </span>
          </div>
          
          {/* Warning Box */}
          <div style={{
            backgroundColor: 'rgba(255,119,119,0.05)',
            border: '1px solid rgba(255,119,119,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080" style={{ flexShrink: 0 }}>
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <span style={{ color: '#ff8080', fontSize: '12px' }}>
              This action cannot be undone.
            </span>
          </div>
          
          {/* Delete Button */}
          <button
            onClick={handleDeleteFirewall}
            disabled={isDeleting}
            style={{
              width: '100%',
              backgroundColor: isDeleting ? '#4a3030' : 'rgba(255,100,100,0.15)',
              border: '1px solid rgba(255,119,119,0.4)',
              color: '#ff7777',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isDeleting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#ff7777',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Deleting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Delete Firewall
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};  // MOBILE/TABLET LAYOUT
  if (isMobile || isTablet) {
    return (
      <div className="flex bg-black min-h-screen text-white">
        <div className={`flex-1 ${isMobile ? 'p-4' : 'p-6'}`}>
          <div className="max-w-6xl mx-auto">
            {/* Header Section */}
            <div className={`mb-6 bg-[#121212] border border-[#333333] rounded-xl shadow-xl ${isMobile ? 'p-4' : 'p-6'}`}>
              <div className={isMobile ? 'space-y-4' : 'flex items-center justify-between'}>
                <div className="flex items-center">
                  <div className={`bg-[#222222] border border-[#333333] rounded-full shadow-sm mr-4 ${isMobile ? 'p-2' : 'p-3'}`}>
                    <ShieldIcon size={isMobile ? 16 : 20} />
                  </div>
                  <div>
                    <h1 className={`font-medium text-white ${isMobile ? 'text-xl' : 'text-2xl'}`}>Firewalls</h1>
                    <p className={`text-gray-400 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      Manage network security for your virtual machines
                    </p>
                  </div>
                </div>
                
                {/* Tablet Actions */}
                {!isMobile && (
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleOpenCreateModal}
                      className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                        isAtLimit 
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                      }`}
                      disabled={loadingStates.creating || isAtLimit}
                    >
                      <FaPlus className="mr-2" /> Create Firewall
                    </button>
                    
                    <div className="relative" ref={regionDropdownRef}>
                      <div 
                        className="w-36 px-3 py-2 bg-[#1d1d1d] border border-[#333333] hover:border-[#cad9e3] rounded-md text-white text-sm flex justify-between items-center cursor-pointer"
                        onClick={toggleRegionDropdown}
                      >
                        <span>{selectedRegion}</span>
                        <div className="flex space-x-0.5">
                          {[0, 1, 2].map((i) => (
                            <div key={i} className="w-1 h-1 bg-[#cad9e3] rounded-full" />
                          ))}
                        </div>
                      </div>
                      
                      {isRegionDropdownOpen && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg overflow-hidden">
                          {regions.map((region) => (
                            <div 
                              key={region} 
                              className={`px-3 py-2 cursor-pointer hover:bg-[#252525] ${
                                region === selectedRegion ? 'bg-[#252525] text-white' : 'text-gray-200'
                              }`}
                              onClick={() => handleRegionChange(region)}
                            >
                              {region}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                  <button
  onClick={handleRefresh}
  className="flex items-center px-3 py-2 bg-[#333333] hover:bg-[#444444] rounded-md text-sm transition-colors relative overflow-hidden"
  disabled={loadingStates.firewalls}
>
  <div 
    className={`absolute top-0 left-0 h-full bg-gradient-to-r from-[#cad9e3] to-[#b8c7d1] transition-all duration-300 rounded-md ${
      loadingStates.firewalls ? 'w-full' : 'w-0'
    }`}
  ></div>
  
  <div className="relative z-10 flex items-center">
    <svg 
      className={`mr-2 w-4 h-4 ${loadingStates.firewalls ? 'animate-spin' : ''}`} 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
      />
    </svg>
    <span className={loadingStates.firewalls ? 'text-black' : 'text-white'}>
      Refresh
    </span>
  </div>
</button>
                  </div>
                )}
              </div>

              {/* Mobile Actions */}
              {isMobile && (
                <div className="mt-4 space-y-3">
                  <div className="flex space-x-3">
                    <button
                      onClick={handleOpenCreateModal}
                      className={`flex-1 flex items-center justify-center px-3 py-2.5 rounded-md font-medium text-sm ${
                        isAtLimit 
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'bg-[#e0eacc] text-black'
                      }`}
                      disabled={loadingStates.creating || isAtLimit}
                    >
                      <FaPlus className="mr-2" size={12} /> Create
                    </button>
                    
                  <button
  onClick={handleRefresh}
  className="flex items-center px-3 py-2 bg-[#333333] hover:bg-[#444444] rounded-md text-sm transition-colors relative overflow-hidden"
  disabled={loadingStates.firewalls}
>
  <div 
    className={`absolute top-0 left-0 h-full bg-gradient-to-r from-[#cad9e3] to-[#b8c7d1] transition-all duration-300 rounded-md ${
      loadingStates.firewalls ? 'w-full' : 'w-0'
    }`}
  ></div>
  
  <div className="relative z-10 flex items-center">
    <svg 
      className={`mr-2 w-4 h-4 ${loadingStates.firewalls ? 'animate-spin' : ''}`} 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
      />
    </svg>
    <span className={loadingStates.firewalls ? 'text-black' : 'text-white'}>
      Refresh
    </span>
  </div>
</button>
                  </div>
                  
                  <div className="relative" ref={regionDropdownRef}>
                    <div 
                      className="w-full px-3 py-2.5 bg-[#1d1d1d] border border-[#333333] rounded-md text-white text-sm flex justify-between items-center cursor-pointer"
                      onClick={toggleRegionDropdown}
                    >
                      <span>Region: {selectedRegion}</span>
                      <div className="flex space-x-0.5">
                        {[0, 1, 2].map((i) => (
                          <div key={i} className="w-1 h-1 bg-[#cad9e3] rounded-full" />
                        ))}
                      </div>
                    </div>
                    
                    {isRegionDropdownOpen && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg">
                        {regions.map((region) => (
                          <div 
                            key={region} 
                            className={`px-3 py-2.5 cursor-pointer ${
                              region === selectedRegion ? 'bg-[#252525] text-white' : 'text-gray-200'
                            }`}
                            onClick={() => handleRegionChange(region)}
                          >
                            {region}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Search and Sort Bar */}
            <div className="flex gap-2 items-center mb-4">
              <div className="flex items-center bg-[#1a1a1a] border border-[#333] rounded-md px-3 py-2 flex-1">
                <FaSearch size={12} className="text-[#6b7280]" />
         <input
  type="text"
  placeholder="Search firewalls..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  maxLength={30}  // or whatever the actual limit is
  className="bg-transparent border-none outline-none text-white ml-2 text-sm w-full"
/>
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="p-0 bg-transparent border-none">
                    <FaTimes size={10} className="text-[#6b7280]" />
                  </button>
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex items-center gap-1 px-2 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-[#9ca3af] text-xs"
                >
                  <FaSort size={10} />
                  <span className="hidden sm:inline">{sortOptions.find(s => s.id === sortBy)?.label}</span>
                </button>
                {sortDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-[#1a1a1a] border border-[#333] rounded-md overflow-hidden z-50 min-w-[80px]">
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        className={`px-3 py-2 text-xs cursor-pointer ${
                          sortBy === s.id ? 'text-[#e0eacc] bg-[rgba(224,234,204,0.1)]' : 'text-[#9ca3af] hover:bg-[#252525]'
                        }`}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Firewall count */}
            <div className="text-[#6b7280] text-xs mb-3">
              {filteredFirewalls.length} firewall{filteredFirewalls.length !== 1 ? 's' : ''}
            </div>

          {/* Loading State */}
            {loadingStates.firewalls ? (
              <div className="bg-[#1a1a1a] border border-[#333333] rounded-xl flex items-center justify-center p-8">
                <div className="w-8 h-8 border-4 border-t-[#e0eacc] border-[#e0eacc] border-opacity-20 rounded-full animate-spin"></div>
                <p className="text-gray-400 ml-4 text-sm">Loading firewalls...</p>
              </div>
            ) : filteredFirewalls.length === 0 ? (
              /* Empty State */
              <div className="bg-[#121212] rounded-xl border border-[#333333] p-6">
                <div className="flex flex-col items-center justify-center py-[70px]">
                  <div className="mb-6">
                    <ShieldIcon size={40} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">No Firewalls Found</h3>
                  <p className="text-gray-400 text-center max-w-md text-sm">
                    {searchQuery 
                      ? `No firewalls matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any firewalls in ${selectedRegion} yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Firewall List */
              <div>
                {/* List Header */}
                <div className="flex justify-between items-center px-4 py-3 bg-[#1a1a1a] rounded-t-xl border border-[#333] border-b-0">
                  <div className="flex items-center gap-2 text-[#e0eacc] text-sm font-semibold">
                    <ShieldIcon size={16} />
                    Firewalls
                    <span className="bg-[#333] px-2 py-0.5 rounded-full text-xs text-[#9ca3af] font-normal">
                      {filteredFirewalls.length}
                    </span>
                  </div>
                  <div className="text-xs text-[#6b7280]">
                    {filteredFirewalls.reduce((sum, fw) => sum + (fw.rules?.length || 0), 0)} total rules
                  </div>
                </div>
                
                {/* Firewall Rows */}
                <div className="bg-[#121212] border border-[#333] border-t-0 rounded-b-xl overflow-hidden">
                  {paginatedFirewalls.map(renderFirewallRow)}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center px-4 py-3 bg-[#141414] border border-[#333] border-t-0 rounded-b-xl mt-[-1px]">
                    <span className="text-xs text-[#6b7280]">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredFirewalls.length)} of {filteredFirewalls.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`w-7 h-7 rounded flex items-center justify-center border-none ${
                          currentPage === 1 ? 'bg-[#1a1a1a] text-[#444] cursor-not-allowed' : 'bg-[#333] text-white cursor-pointer'
                        }`}
                      >
                        <FaChevronLeft size={10} />
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[28px] h-7 px-1.5 rounded border-none text-xs cursor-pointer ${
                            currentPage === page ? 'bg-[#e0eacc] text-black font-semibold' : 'bg-[#252525] text-[#9ca3af]'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`w-7 h-7 rounded flex items-center justify-center border-none ${
                          currentPage === totalPages ? 'bg-[#1a1a1a] text-[#444] cursor-not-allowed' : 'bg-[#333] text-white cursor-pointer'
                        }`}
                      >
                        <FaChevronRight size={10} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {createModalOpen && (
          <FirewallCreateModal
            isOpen={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            environments={environments}
            onFirewallCreated={handleFirewallCreated}
            existingFirewallNames={firewalls.map(fw => fw.name)}
          />
        )}
        
        {detailsModalOpen && selectedFirewall && (
          <FirewallDetailsModal
            isOpen={detailsModalOpen}
            onClose={() => setDetailsModalOpen(false)}
            firewall={selectedFirewall}
            onFirewallUpdated={debouncedFetchFirewalls}
          />
        )}
        
        {ruleModalOpen && selectedFirewall && (
          <FirewallRuleModal
            isOpen={ruleModalOpen}
            onClose={() => setRuleModalOpen(false)}
            firewall={selectedFirewall}
            onRuleAdded={debouncedFetchFirewalls}
          />
        )}
        
        {attachModalOpen && selectedFirewall && (
          <FirewallAttachModal
            isOpen={attachModalOpen}
            onClose={() => setAttachModalOpen(false)}
            firewall={selectedFirewall}
            onAttachmentUpdated={debouncedFetchFirewalls}
          />
        )}
        
        <ConfirmDeleteDialog />
      </div>
    );
  }

  // DESKTOP LAYOUT
  return (
    <div className="flex bg-black min-h-screen text-white">
      <Sidebar 
        activePage="firewall" 
        isMobile={isMobile}
        isTablet={isTablet}
        isSidebarOpen={false}
        toggleSidebar={() => {}}
        authenticated={true}
        onAuthAction={() => {}}
      />
      
      <div className="flex-1">
        <div className="max-w-none w-[118%] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex justify-between items-center py-6 border-b border-[#333333]">
            <div className="flex items-center">
              <div className="mr-4">
                <ShieldIcon size={20} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Firewalls</h1>
                <p className="text-gray-400 text-sm">Manage network security for your virtual machines</p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleOpenCreateModal}
                className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                  isAtLimit 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                }`}
                disabled={loadingStates.creating || isAtLimit}
                title={isAtLimit ? `Maximum limit of 50 firewalls reached in ${selectedRegion}` : ''}
              >
                <FaPlus className="mr-2" /> Create Firewall
              </button>
              
              <div className="relative" ref={regionDropdownRef}>
                <div 
                  className="w-36 px-3 py-3 bg-[#1d1d1d] border border-[#333333] hover:border-[#cad9e3] rounded-md text-white text-sm flex justify-between items-center cursor-pointer transition-colors"
                  onClick={toggleRegionDropdown}
                >
                  <span>{selectedRegion}</span>
                  <div className="flex space-x-0.5">
                    {[0, 1, 2].map((i) => (
                      <div 
                        key={i}
                        className={`w-1 h-1 bg-[#cad9e3] rounded-full ${isRegionDropdownOpen ? 'animate-bounce' : ''}`}
                        style={{ animationDelay: `${i * 100}ms` }}
                      />
                    ))}
                  </div>
                </div>
                
                {isRegionDropdownOpen && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg overflow-hidden">
                    <div className="py-1">
                      {regions.map((region) => (
                        <div 
                          key={region} 
                          className={`mx-2 px-2 py-1.5 cursor-pointer transition-all border border-transparent rounded hover:border-[#cad9e3] ${
                            region === selectedRegion 
                              ? 'bg-[#252525] text-white border-[#cad9e3]' 
                              : 'text-gray-200 hover:bg-[#252525]'
                          }`}
                          onClick={() => handleRegionChange(region)}
                        >
                          {region}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
           <button
  onClick={handleRefresh}
  className="flex items-center justify-center px-4 py-3 bg-[#333333] hover:bg-[#444444] rounded-lg text-sm transition-colors relative overflow-hidden"
  disabled={loadingStates.firewalls}
>
  <div 
    className={`absolute top-0 left-0 h-full bg-gradient-to-r from-[#cad9e3] to-[#b8c7d1] transition-all duration-300 rounded-lg ${
      loadingStates.firewalls ? 'w-full' : 'w-0'
    }`}
  ></div>
  
  <div className="relative z-10 flex items-center">
    <svg 
      className={`mr-2 w-4 h-4 ${loadingStates.firewalls ? 'animate-spin' : ''}`} 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
      />
    </svg>
    <span className={loadingStates.firewalls ? 'text-black' : 'text-white'}>
      Refresh
    </span>
  </div>
</button>
            </div>
          </div>
          
          <div className="py-6" style={{ minHeight: '120vh' }}>
            {/* Search and Sort Bar */}
            <div className="flex gap-3 items-center mb-4">
              <div className="flex items-center bg-[#1a1a1a] border border-[#333] rounded-md px-3 py-2 flex-1 max-w-[300px]">
                <FaSearch size={14} className="text-[#6b7280]" />
             <input
  type="text"
  placeholder="Search firewalls..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  maxLength={30}  // or whatever the actual limit is
  className="bg-transparent border-none outline-none text-white ml-2 text-sm w-full"
/>
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="p-0 bg-transparent border-none cursor-pointer">
                    <FaTimes size={12} className="text-[#6b7280]" />
                  </button>
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-[#9ca3af] text-xs cursor-pointer hover:border-[#555]"
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-[#1a1a1a] border border-[#333] rounded-md overflow-hidden z-50 min-w-[100px]">
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        className={`px-3 py-2 text-xs cursor-pointer ${
                          sortBy === s.id ? 'text-[#e0eacc] bg-[rgba(224,234,204,0.1)]' : 'text-[#9ca3af] hover:bg-[#252525]'
                        }`}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="ml-auto text-[#6b7280] text-xs">
                {filteredFirewalls.length} firewall{filteredFirewalls.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* Loading State */}
            {loadingStates.firewalls ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-12 h-12 border-4 border-t-[#e0eacc] border-[#e0eacc] border-opacity-20 rounded-full animate-spin"></div>
                <p className="text-gray-400 ml-4">Loading firewalls...</p>
              </div>
            ) : filteredFirewalls.length === 0 ? (
              /* Empty State */
<div className="bg-[#121212] rounded-[10px] p-8 border border-[#333333] shadow-[0_6px_24px_rgba(0,0,0,0.4)]">
  <div className="flex flex-col items-center justify-center py-[90px]">                  <div className="mb-6">
                    <ShieldIcon size={48} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">No Firewalls Found</h3>
                  <p className="text-gray-400 text-center max-w-md">
                    {searchQuery 
                      ? `No firewalls matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any firewalls in ${selectedRegion} yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Firewall List */
              <div>
                {/* List Header */}
                <div className="flex justify-between items-center px-4 py-3 bg-[#1a1a1a] rounded-t-[10px] border border-[#333] border-b-0">
                  <div className="flex items-center gap-2.5 text-[#e0eacc] text-sm font-semibold">
                    <ShieldIcon size={16} />
                    Firewalls
                    <span className="bg-[#333] px-2 py-0.5 rounded-full text-xs text-[#9ca3af] font-normal">
                      {filteredFirewalls.length}
                    </span>
                  </div>
                  <div className="text-xs text-[#6b7280]">
                    {filteredFirewalls.reduce((sum, fw) => sum + (fw.rules?.length || 0), 0)} total rules
                  </div>
                </div>
                
                {/* Firewall Rows */}
                <div className="bg-[#121212] border border-[#333] border-t-0 overflow-hidden" style={{ borderBottomLeftRadius: totalPages > 1 ? '0' : '10px', borderBottomRightRadius: totalPages > 1 ? '0' : '10px' }}>
                  {paginatedFirewalls.map(renderFirewallRow)}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center px-4 py-3 bg-[#141414] border border-[#333] border-t-[#2a2a2a] rounded-b-[10px]">
                    <span className="text-xs text-[#6b7280]">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredFirewalls.length)} of {filteredFirewalls.length}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`w-7 h-7 rounded flex items-center justify-center border-none ${
                          currentPage === 1 ? 'bg-[#1a1a1a] text-[#444] cursor-not-allowed' : 'bg-[#333] text-white cursor-pointer hover:bg-[#444]'
                        }`}
                      >
                        <FaChevronLeft size={10} />
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[28px] h-7 px-1.5 rounded border-none cursor-pointer text-xs ${
                            currentPage === page ? 'bg-[#e0eacc] text-black font-semibold' : 'bg-[#252525] text-[#9ca3af] hover:bg-[#333]'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`w-7 h-7 rounded flex items-center justify-center border-none ${
                          currentPage === totalPages ? 'bg-[#1a1a1a] text-[#444] cursor-not-allowed' : 'bg-[#333] text-white cursor-pointer hover:bg-[#444]'
                        }`}
                      >
                        <FaChevronRight size={10} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modals */}
      {createModalOpen && (
        <FirewallCreateModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          environments={environments}
          onFirewallCreated={handleFirewallCreated}
          existingFirewallNames={firewalls.map(fw => fw.name)}
        />
      )}
      
      {detailsModalOpen && selectedFirewall && (
        <FirewallDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          firewall={selectedFirewall}
          onFirewallUpdated={debouncedFetchFirewalls}
        />
      )}
      
      {ruleModalOpen && selectedFirewall && (
        <FirewallRuleModal
          isOpen={ruleModalOpen}
          onClose={() => setRuleModalOpen(false)}
          firewall={selectedFirewall}
          onRuleAdded={debouncedFetchFirewalls}
        />
      )}
      
      {attachModalOpen && selectedFirewall && (
        <FirewallAttachModal
          isOpen={attachModalOpen}
          onClose={() => setAttachModalOpen(false)}
          firewall={selectedFirewall}
          onAttachmentUpdated={debouncedFetchFirewalls}
        />
      )}
      
      <ConfirmDeleteDialog />
    </div>
  );
};

export default FirewallPage;